import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // One-time setup: all variables are local to this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) return;

    // Check if dismissed recently
    const dismissedAt = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return;
    }

    // For iOS, show after a delay
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowPrompt(true), 5000);
      return () => clearTimeout(timer);
    }

    // For other browsers, listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50 animate-slide-up">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 md:p-5">
          <button 
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2E7D32] to-[#FBC02D] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">ভূ</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm md:text-base">ভূমি App ইনস্টল করুন</h3>
              <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                Home screen এ যোগ করুন দ্রুত অ্যাক্সেসের জন্য
              </p>
            </div>
          </div>
          
          <Button 
            onClick={handleInstall}
            className="w-full mt-4 bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-lg h-10 text-sm font-medium"
          >
            <Download size={16} className="mr-2" />
            ইনস্টল করুন
          </Button>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-sm p-6 animate-slide-up">
            <button 
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2E7D32] to-[#FBC02D] flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-2xl">ভূ</span>
              </div>
              <h3 className="font-bold text-lg text-gray-900">iOS এ ইনস্টল করুন</h3>
            </div>
            
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 text-[#2E7D32] font-bold text-xs">1</div>
                <p className="text-gray-600 pt-0.5">
                  নিচে <span className="inline-flex items-center"><svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L8 6h3v8h2V6h3L12 2zm6 18H6v-2h12v2z"/></svg></span> Share বাটনে ক্লিক করুন
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 text-[#2E7D32] font-bold text-xs">2</div>
                <p className="text-gray-600 pt-0.5">
                  "Add to Home Screen" অপশনে ক্লিক করুন
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 text-[#2E7D32] font-bold text-xs">3</div>
                <p className="text-gray-600 pt-0.5">
                  "Add" বাটনে ক্লিক করুন
                </p>
              </div>
            </div>
            
            <Button 
              onClick={handleDismiss}
              variant="outline"
              className="w-full mt-6 rounded-lg h-10"
            >
              বুঝেছি
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
